import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import BrandMark from "@/components/BrandMark";
import api from "@/lib/api";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function Login() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("normal@namastenetwork.ai");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [easyLogin, setEasyLogin] = useState({ enabled: true, show_local_form: true, show_role_buttons: true });
  const [oauthConfig, setOauthConfig] = useState({ google_enabled: false, apple_enabled: false });
  const [oauthRole, setOauthRole] = useState("user");
  const localDemoPassword = "nnai123";
  const localLoginAllowed = easyLogin.enabled !== false && easyLogin.show_local_form !== false && easyLogin.ip_allowed !== false;

  useEffect(() => {
    api.get("/app-options/easy_login")
      .then((res) => setEasyLogin((current) => ({ ...current, ...(res.data?.value || {}) })))
      .catch(() => {});
    api.get("/auth/oauth/config")
      .then((res) => setOauthConfig(res.data || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      const from = location.state?.from || "/";
      navigate(from, { replace: true });
    }
  }, [user, navigate, location]);

  const finishOAuth = async (provider, idToken) => {
    if (!idToken) {
      toast.error(`${provider === "apple" ? "Apple" : "Google"} did not return a sign-in token`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/oauth", { provider, id_token: idToken, role: oauthRole });
      localStorage.setItem("nn_session_token", data.session_token);
      setUser(data.user);
      toast.success(`Signed in as ${data.user.name}`);
      navigate(location.state?.from || "/", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || `${provider === "apple" ? "Apple" : "Google"} sign-in failed`);
    } finally {
      setBusy(false);
    }
  };

  const loadScript = (id, src) => new Promise((resolve, reject) => {
    if (document.getElementById(id)) return resolve();
    const script = document.createElement("script");
    script.id = id;
    script.src = src;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });

  const handleGoogle = async () => {
    if (!oauthConfig.google_web_client_id) {
      toast.error("Google client ID is not configured yet");
      return;
    }
    try {
      await loadScript("google-identity-services", "https://accounts.google.com/gsi/client");
      window.google.accounts.id.initialize({
        client_id: oauthConfig.google_web_client_id,
        callback: (response) => finishOAuth("google", response.credential),
      });
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          window.google.accounts.id.renderButton(
            document.getElementById("google-signin-render-target"),
            { theme: "filled_black", size: "large", width: 340 }
          );
        }
      });
    } catch {
      toast.error("Could not start Google sign-in");
    }
  };

  const handleApple = async () => {
    if (!oauthConfig.apple_web_client_id) {
      toast.error("Apple web client ID is not configured yet");
      return;
    }
    try {
      await loadScript("apple-signin-js", "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js");
      window.AppleID.auth.init({
        clientId: oauthConfig.apple_web_client_id,
        scope: "name email",
        redirectURI: window.location.origin + "/login",
        state: crypto.randomUUID?.() || String(Date.now()),
        usePopup: true,
      });
      const result = await window.AppleID.auth.signIn();
      await finishOAuth("apple", result?.authorization?.id_token);
    } catch (err) {
      if (err?.error !== "popup_closed_by_user") {
        toast.error("Could not start Apple sign-in");
      }
    }
  };

  const handleLocalLogin = async (e) => {
    e.preventDefault();
    await submitLocalLogin(email, password);
  };

  const submitLocalLogin = async (loginEmail, loginPassword) => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/local", { email: loginEmail, password: loginPassword });
      localStorage.setItem("nn_session_token", data.session_token);
      setUser(data.user);
      toast.success(`Signed in as ${data.user.name}`);
      navigate(location.state?.from || "/", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const quickLogin = async (loginEmail) => {
    setEmail(loginEmail);
    setPassword(localDemoPassword);
    await submitLocalLogin(loginEmail, localDemoPassword);
  };

  return (
    <div className="min-h-screen grid place-items-center bg-background relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.18),transparent_60%)]" />
      <div className="relative w-full max-w-md p-8 mx-5 rounded-3xl border border-border bg-card" data-testid="login-card">
        <div className="flex items-center gap-3">
          <BrandMark size={48} />
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] font-black text-primary">Welcome</p>
            <h1 className="font-display text-2xl font-extrabold">NamasteNetwork.ai</h1>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-5">
          Sign in to book seats, save your favourites, and create events for the UK Indian community.
        </p>
        {(oauthConfig.google_enabled || oauthConfig.apple_enabled) && (
          <div className="mt-6">
            <p className="text-[11px] uppercase tracking-[0.2em] font-black text-muted-foreground">Account type</p>
            <div className="mt-2 grid grid-cols-2 gap-2 rounded-full border border-border p-1">
              {[
                ["user", "User"],
                ["organizer", "Organiser"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setOauthRole(value)}
                  className={`rounded-full px-3 py-2 text-sm font-bold ${oauthRole === value ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
        {localLoginAllowed && (
          <form onSubmit={handleLocalLogin} className="mt-6 space-y-3" data-testid="local-login-form">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              autoComplete="email"
              data-testid="local-login-email"
            />
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoComplete="current-password"
              data-testid="local-login-password"
            />
            <Button type="submit" disabled={busy} data-testid="local-login-btn" className="w-full h-12 rounded-full font-bold">
              {busy ? "Signing in..." : "Sign in"}
            </Button>
            {easyLogin.show_role_buttons !== false && (
              <div className="grid grid-cols-3 gap-2">
                {[
                  ["User", "normal@namastenetwork.ai"],
                  ["Organiser", "organiser@namastenetwork.ai"],
                  ["Admin", "admin@namastenetwork.ai"],
                ].map(([label, value]) => (
                  <button
                    key={value}
                    type="button"
                    disabled={busy}
                    onClick={() => quickLogin(value)}
                    className="rounded-full border border-border px-2 py-1.5 text-[11px] font-bold hover:bg-muted"
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </form>
        )}
        {oauthConfig.google_enabled && (
          <>
            <Button onClick={handleGoogle} disabled={busy} data-testid="google-signin-btn" className="mt-6 w-full h-12 rounded-full font-bold">
              Continue with Google
            </Button>
            <div id="google-signin-render-target" className="mt-3 grid place-items-center" />
          </>
        )}
        {oauthConfig.apple_enabled && (
          <Button onClick={handleApple} disabled={busy} data-testid="apple-signin-btn" variant="outline" className="mt-3 w-full h-12 rounded-full font-bold">
            Continue with Apple
          </Button>
        )}
        {!oauthConfig.google_enabled && !oauthConfig.apple_enabled && easyLogin.enabled === false && (
          <p className="mt-6 rounded-2xl border border-border p-3 text-xs text-muted-foreground">
            Google and Apple sign-in need provider client IDs before production login can be used.
          </p>
        )}
        <button onClick={() => navigate("/")} data-testid="continue-as-guest" className="mt-4 w-full text-center text-xs text-muted-foreground hover:text-foreground">
          Continue browsing as guest
        </button>
        {localLoginAllowed && (
          <p className="mt-6 text-[11px] text-muted-foreground">
            Local sign-in is for staging while production authentication is being configured.
          </p>
        )}
      </div>
    </div>
  );
}
